import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../config/firebase';
import { formatPhoneNumber, splitPhoneNumbers, isLandlineNumber, isValidLength } from '../utils/phoneUtils';
import './ServiceProviderCard.css';

const ServiceProviderCard = ({ provider, isAdmin, onDelete, isLoading }) => {
  const [user] = useState(auth.currentUser);

  if (isLoading) {
    return (
      <div className="provider-card loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const renderPhoneNumbers = () => {
    if (!provider.phoneNumber && !provider.phoneNumber2) return null;

    // If we have phoneNumber2, display both numbers separately
    if (provider.phoneNumber2) {
      return (
        <>
          <div className="contact-item clickable" onClick={(e) => handlePhoneClick(provider.phoneNumber, e)}>
            <i className="fas fa-phone"></i>
            <span>Primary: {provider.phoneNumber}</span>
          </div>
          <div className="contact-item clickable" onClick={(e) => handlePhoneClick(provider.phoneNumber2, e)}>
            <i className="fas fa-phone"></i>
            <span>Secondary: {provider.phoneNumber2}</span>
          </div>
        </>
      );
    }

    // For legacy data that might still have numbers with slashes
    const numbers = splitPhoneNumbers(provider.phoneNumber);
    if (numbers.length > 1) {
      return numbers.map((number, index) => (
        <div key={index} className="contact-item clickable" onClick={(e) => handlePhoneClick(number, e)}>
          <i className="fas fa-phone"></i>
          <span>{index === 0 ? 'Primary: ' : 'Secondary: '}{number}</span>
        </div>
      ));
    }

    // Single number case
    return (
      <div className="contact-item clickable" onClick={(e) => handlePhoneClick(provider.phoneNumber, e)}>
        <i className="fas fa-phone"></i>
        <span>{provider.phoneNumber}</span>
      </div>
    );
  };

  const handlePhoneClick = (phoneNumber, e) => {
    e.preventDefault();
    const formattedPhone = formatPhoneNumber(phoneNumber);
    const isLandline = isLandlineNumber(phoneNumber);
    const hasValidLength = isValidLength(phoneNumber);
    
    const modal = document.createElement('div');
    modal.className = 'contact-modal';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="contact-modal-close">×</div>
        <h3>Contact<br><strong>${provider.name}</strong></h3>
        <div class="modal-options">
          <a href="tel:${formattedPhone}" class="modal-option">
            <i class="fas fa-phone"></i>
            Call
          </a>
          ${(!isLandline && hasValidLength) ? `
            <a href="https://wa.me/${formattedPhone.replace('+', '')}" 
               target="_blank" 
               rel="noopener noreferrer" 
               class="modal-option">
              <i class="fab fa-whatsapp"></i>
              WhatsApp
            </a>
          ` : `
            <div class="modal-option disabled">
              <i class="fab fa-whatsapp"></i>
              ${!hasValidLength ? 'Invalid Number' : 'Landline (No WhatsApp)'}
            </div>
          `}
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('.contact-modal-close').onclick = () => {
      document.body.removeChild(modal);
    };
  };

  const handleEmailClick = (email) => {
    const userName = user ? (user.firstName && user.lastName ? 
      `${user.firstName} ${user.lastName}` : 
      user.displayName || 'a potential client') : 'a potential client';
      
    const subject = `Service Inquiry - ${provider.name}`;
    const body = `Hi ${provider.name},\n\nI am ${userName} and I'm querying about your services.`;
    
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleAddressClick = (address, e) => {
    e.preventDefault();
    const encodedAddress = encodeURIComponent(address);
    
    const modal = document.createElement('div');
    modal.className = 'contact-modal';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="contact-modal-close">×</div>
        <h3>Navigate to ${provider.name}</h3>
        <div class="modal-options">
          <a href="https://www.google.com/maps/search/?api=1&query=${encodedAddress}" 
             target="_blank" 
             rel="noopener noreferrer" 
             class="modal-option">
            <i class="fas fa-map-marker-alt"></i>
            Google Maps
          </a>
          <a href="https://waze.com/ul?q=${encodedAddress}" 
             target="_blank" 
             rel="noopener noreferrer" 
             class="modal-option">
            <i class="fas fa-road"></i>
            Waze
          </a>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('.contact-modal-close').onclick = () => {
      document.body.removeChild(modal);
    };
  };

  const renderStars = (rating) => {
    const stars = [];
    const roundedRating = Math.round(rating * 2) / 2; // Round to nearest 0.5
    
    for (let i = 1; i <= 5; i++) {
      if (i <= roundedRating) {
        stars.push(<span key={i} className="star filled">★</span>);
      } else if (i - 0.5 === roundedRating) {
        stars.push(<span key={i} className="star half">★</span>);
      } else {
        stars.push(<span key={i} className="star">★</span>);
      }
    }
    return stars;
  };

  const isCurrentlyOpen = () => {
    if (!provider.businessHours) return false;
    
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = days[now.getDay()]; // Get current day name
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const dayHours = provider.businessHours[currentDay];
    if (!dayHours || !dayHours.isOpen) return false;
    
    return currentTime >= dayHours.open && currentTime <= dayHours.close;
  };

  return (
    <div className="provider-card">
      {provider.isPriorityListing && (
        <div className="priority-badge">
          <i className="fas fa-star"></i>
        </div>
      )}
      <h3>{provider.name}</h3>
      <p>Category: {provider.category}</p>
      <p className="location">
        <i className="fas fa-map-marker-alt"></i>
        {provider.location || 'Location not specified'}
      </p>
      <div className="rating">
        {provider.averageRating ? (
          <>
            <div className="stars">{renderStars(provider.averageRating)}</div>
            <span className="review-count">({provider.numberOfReviews || 0} reviews)</span>
          </>
        ) : (
          <span>No ratings yet</span>
        )}
      </div>
      
      <div className="contact-details">
        {renderPhoneNumbers()}
        
        {provider.email && provider.email !== 'N/A' && (
          <div className="contact-item clickable" onClick={(e) => handleEmailClick(provider.email, e)}>
            <i className="fas fa-envelope"></i>
            <span>{provider.email}</span>
          </div>
        )}
        
        {provider.address && provider.address !== 'N/A' && (
          <div className="contact-item clickable" onClick={(e) => handleAddressClick(provider.address, e)}>
            <i className="fas fa-map-marker-alt"></i>
            <span>{provider.address}</span>
          </div>
        )}
      </div>

      <Link 
        to={`/providers/${provider.id}`} 
        className="view-profile-btn"
        state={{ provider }}
      >
        View Profile
      </Link>
      <div className="business-status">
        <span className={`status-indicator ${isCurrentlyOpen() ? 'open' : 'closed'}`}>
          {isCurrentlyOpen() ? 'Open Now' : 'Closed'}
        </span>
      </div>
    </div>
  );
};

export default ServiceProviderCard;
